import applyPaginationQueryParams from '@forwardflow/helpers/utils/applyPaginationQueryParams'
import { apiDelete, apiGet, apiPatch, apiPost } from '@forwardflow/services/Axios/configs'
import {
   IOrgGeneralDetails,
   PaginatedResponse,
   PaginationQueryParams,
   ResponseSuccess,
} from '@forwardflow/types'
import {
   IAppointmentHoursConfig,
   IBusinessHoursConfig,
   IEmailInvitation,
   IOrganizationDetails,
   IOrganizationList,
   OrganizationUsers,
} from '@forwardflow/types/organization'
import { IBusinessDetails } from '@forwardflow/types/onboarding'
import { IMonthlyBudgetsResponse } from '@forwardflow/types/monthly-budget'
import {
   IAriaAgent,
   IAriaRecipient,
   IAutomationsConfig,
} from '@forwardflow/types/automations'

type IGeneral = {
   timezone: string
   defaultAppointmentLocation: string
   defaultMeetingPlatform: string
   defaultAppointmentType: string
}
export default class OrganizationService {
   static async getOrganizationDetails(queryParams: PaginationQueryParams) {
      const data = await apiGet<ResponseSuccess<IOrganizationDetails>>({
         apiPath: `/organization-account/details?${applyPaginationQueryParams(
            queryParams
         )}`,
      })
      return data.data
   }

   static async updateBusinessDetails(businessDetails: IBusinessDetails) {
      const data = await apiPatch<ResponseSuccess<IBusinessDetails>>({
         apiPath: `/organization-account/business-details?isFromInternal=true`,
         data: businessDetails,
      })
      return data.data
   }

   static async updateGeneralDetails(info: IGeneral) {
      const data = await apiPatch<ResponseSuccess<IOrgGeneralDetails>>({
         apiPath: `/organization-accounts/update-gen-data`,
         data: {
            timezone: info.timezone,
            defaultAppointmentLocation: info.defaultAppointmentLocation,
            defaultMeetingPlatform: info.defaultMeetingPlatform,
            defaultAppointmentType: info.defaultAppointmentType,
         },
      })
      return data.data
   }

   // Get organization users list
   static async getOrganizationAvaiableForInvitationUsersLists(
      queryParams: PaginationQueryParams
   ) {
      const response = await apiGet<PaginatedResponse<OrganizationUsers>>({
         apiPath: `/users/available-for-invitation?${applyPaginationQueryParams(
            queryParams
         )}`,
      })
      return response
   }

   // Get organization users list
   static async getOrganizationUsersLists(queryParams: PaginationQueryParams) {
      const response = await apiGet<PaginatedResponse<OrganizationUsers>>({
         apiPath: `/users?${applyPaginationQueryParams(queryParams)}`,
      })
      return response
   }

   // Get organization business hour data

   static async getBusinessHourData() {
      const data = await apiGet<ResponseSuccess<IBusinessHoursConfig>>({
         apiPath: `/organization-configurations`,
      })
      return data.data
   }

   // Update organization business hour data
   static async updateBusinessHourData(hourData: IBusinessHoursConfig) {
      const data = await apiPatch<ResponseSuccess<IBusinessHoursConfig>>({
         apiPath: `/organization-configurations/business-hours`,
         data: {
            allowAgentsToSetAvailability:
               hourData.businessHours.allowAgentsToSetAvailability,
            days: hourData.businessHours.days,
            timezone: hourData.businessHours.timezone,
         },
      })
      return data.data
   }

   // Update appointment timeframes data
   static async updateAppointmentTimeframesData(
      hourData: IAppointmentHoursConfig
   ) {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/organization-configurations/appointment-timeframe`,
         data: {
            allowAgentsToSetAvailability:
               hourData.appointmentTimeframes?.allowAgentsToSetAvailability,
            days: hourData.appointmentTimeframes?.days,
            timezone: hourData.appointmentTimeframes?.timezone,
            appointmentBufferTime:
               hourData.appointmentTimeframes?.appointmentBufferTime,
            appointmentType: hourData.appointmentTimeframes?.appointmentType,
            meetingPlatform: hourData.appointmentTimeframes?.meetingPlatform,
            meetingLength: hourData.appointmentTimeframes?.meetingLength,
            meetingLink: hourData.appointmentTimeframes?.meetingLink,
         },
      })
      return data.data
   }

   // Update organization business hour data
   static async updateAppointmentTimeframes(hourData: IBusinessHoursConfig) {
      const data = await apiPatch<ResponseSuccess<IBusinessHoursConfig>>({
         apiPath: `/organization-configurations/appointment-timeframes`,
         data: {
            appointmentBufferTime: hourData.appointmentTimeframes,
            businessHours: hourData.businessHours,
         },
      })
      return data.data
   }

   // Get list of Organization list
   static async getOrganization(queryParams: PaginationQueryParams) {
      const data = await apiGet<ResponseSuccess<IOrganizationList[]>>({
         apiPath: `/organizations?${applyPaginationQueryParams(queryParams)}`,
         includeXOrg: false,
      })
      return data.data
   }

   // Connect to Organization
   static async connectOrganization(organizationId: string) {
      const data = await apiPost<ResponseSuccess<IOrganizationList>>({
         apiPath: `/users/connect-to-org `,
         data: {
            orgId: organizationId,
         },
         includeXOrg: false,
      })
      return data.data
   }

   // TODO: Update the API path once the backend endpoint is ready
   static async deleteUserInvitation(userId: string | number) {
      const data = await apiDelete({
         apiPath: `/users/${userId}/invitation`,
      })
      return data
   }

   static async updateChannelConfig(config: {
      phoneCallEnabled?: boolean
      voicemailEnabled?: boolean
      smsEnabled?: boolean
   }) {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/organization-account/channel-config`,
         data: config,
      })
      return data.data
   }

   static async patchWhitelistAPI(whitelistPayload: IEmailInvitation) {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/users/update-white-list-status`,
         data: whitelistPayload,
      })
      return data.data
   }

   static async getDefaultConfigurations() {
      const response = await apiGet<
         ResponseSuccess<{ elevenLabsApiKey: string; geminiApiKey: string; featureFlags: any }>
      >({
         apiPath: `default-configuration`,
      })
      return response?.data
   }

   // Install Inbox App for an organization
   static async installInboxApp(userId: number) {
      const data = await apiPost<ResponseSuccess<any>>({
         apiPath: `/organization-account/install-inbox-app`,
         data: { userId },
      })
      return data.data
   }

   // Uninstall Inbox App for an organization
   static async uninstallInboxApp(userId: number) {
      const data = await apiPost<ResponseSuccess<any>>({
         apiPath: `/organization-account/uninstall-inbox-app`,
         data: { userId },
      })
      return data.data
   }

   // Fetch stage mappings for an organization
   static async getStageMappings() {
      const data = await apiGet<ResponseSuccess<any>>({
         apiPath: `/stage-mappings`,
      })
      return data.data
   }

   // Update payment method
   static async updatePaymentMethod(paymentInfo: {
      cardId: string
      nameOnCard: string
      country: string
      addressLineOne: string
      city: string
      postalCode: string
   }) {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/organization-account/update-payment-method`,
         data: {
            paymentInfo,
         },
      })
      return data.data
   }

   // Get monthly budgets for organization
   static async getMonthlyBudgets() {
      const data = await apiGet<ResponseSuccess<IMonthlyBudgetsResponse>>({
         apiPath: `/organization-account/monthly-budgets`,
      })
      return data.data
   }

   // Update automations configuration
   static async updateAutomations(config: Partial<IAutomationsConfig>) {
      const data = await apiPatch<ResponseSuccess<IAutomationsConfig>>({
         apiPath: `/organization-account/automations`,
         data: config,
      })
      return data.data
   }

   /** Aria intro preview for the given recipient emails. X-ORG auto. */
   static async getEligibleAriaAgents(emails: string[]) {
      const emailsParam = encodeURIComponent(emails.join(','))
      const res = await apiGet<
         ResponseSuccess<{ data: IAriaAgent[]; config: unknown; metadata: unknown }>
      >({
         apiPath: `/realtor-automations/daily-sync-data?type=introduction&emails=${emailsParam}`,
      })
      return res.data?.data ?? []
   }

   /** All accepted-invitation users — the recipient picker source (plan 0007). */
   static async getAutomationRecipients() {
      const res = await apiGet<ResponseSuccess<IAriaRecipient[]>>({
         apiPath: `/realtor-automations/recipients`,
      })
      return res.data ?? []
   }

   /** "Say hi from Aria" — emit an introduction to a single agent (by email). */
   static async sayHiFromAria(orgId: string, agentEmail: string) {
      return apiPost({
         apiPath: `/svc/orchestrator/introduction?orgId=${encodeURIComponent(
            orgId
         )}&agentEmail=${encodeURIComponent(agentEmail)}`,
      })
   }

   // Update monthly budgets for organization
   static async updateMonthlyBudgets(budgets: {
      newLeadBudget: number
      reactivateLeadBudget: number
   }) {
      const data = await apiPatch<ResponseSuccess<IMonthlyBudgetsResponse>>({
         apiPath: `/organization-account/monthly-budgets`,
         data: budgets,
      })
      return data.data
   }
}
